'use client';

import { useContext, useMemo } from 'react';
import { Suggestion, SuggestionGroup } from '@tranquility-ai/theme-components/suggestion';
import { SUGGESTED_PROMPT_IDS } from '@/i18n/promptCatalog';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import {
  ChatEvidenceLockerContext,
} from './chat-evidence-locker-context-new';

type SuggestedPrompt = { id: string; label: string; query: string };

export type PromptSuggestionsNewProps = {
  visible?: boolean;
  /** When set, overrides the evidence-locker composer query setter. */
  onSelectPrompt?: (query: string) => void;
  showPromptLibrary?: boolean;
  onOpenPromptLibrary?: () => void;
};

export function PromptSuggestionsNew({
  visible = true,
  onSelectPrompt,
  showPromptLibrary = false,
  onOpenPromptLibrary,
}: PromptSuggestionsNewProps) {
  const t = useTimePilotT();
  const evidenceLocker = useContext(ChatEvidenceLockerContext);

  const prompts = useMemo((): SuggestedPrompt[] => {
    const suggestedIds: readonly string[] = SUGGESTED_PROMPT_IDS;
    if (suggestedIds.length === 0) {
      return [{
        id: 'placeholder',
        label: t('chat.suggestions.placeholderLabel'),
        query: t('chat.suggestions.placeholderQuery'),
      }];
    }
    return suggestedIds.map((id) => ({
      id,
      label: t(`prompts.suggested.${id}.label`),
      query: t(`prompts.suggested.${id}.query`),
    }));
  }, [t]);

  if (!visible) return null;

  const handleSelect = (query: string) => {
    if (onSelectPrompt) {
      onSelectPrompt(query);
      return;
    }
    if (!evidenceLocker) {
      throw new Error(
        'PromptSuggestionsNew requires onSelectPrompt or ChatEvidenceLockerProvider',
      );
    }
    evidenceLocker.setComposerQuery(query);
  };

  return (
    <div className="tp-prompt-suggestions box-border mt-4 flex w-screen flex-col items-center justify-start sm:justify-center">
      <div className="box-border flex w-screen flex-row items-center justify-start gap-2 overflow-x-auto px-[20px] scrollbar-hide sm:justify-center sm:gap-4 sm:overflow-x-hidden sm:px-0">
        <h3 className="mb-1 hidden text-lg font-normal text-white/65 sm:mb-4 sm:flex">
          {t('chat.suggestions.title')}:
        </h3>
        <SuggestionGroup
          layout="row"
          aria-label={t('chat.suggestions.title')}
          className="min-w-0 flex-1 sm:flex-none"
        >
          {prompts.map((prompt) => (
            <Suggestion
              key={prompt.id}
              layout="pill"
              size="md"
              label={prompt.label}
              onClick={() => handleSelect(prompt.query)}
              className="max-w-[280px]"
            />
          ))}
          {showPromptLibrary && onOpenPromptLibrary ? (
            <Suggestion
              layout="pill"
              size="md"
              label={t('chat.suggestions.seeAll')}
              onClick={onOpenPromptLibrary}
              className="shrink-0"
            />
          ) : null}
        </SuggestionGroup>
      </div>
    </div>
  );
}
